import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Car, User, AlertTriangle, Search, PenLine, File, Loader2, CheckCircle, Paperclip, Lock, FileText } from 'lucide-react';
import { API_BASE_URL, getActiveOrg } from '../../api/core.js';
import api, { vehicleInsuranceAPI } from '../../api.js';
import { documentAPI } from '../../api/documents.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { useTranslation } from 'react-i18next';

// Allowed file extensions and max size for claim documents
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// Claim document types (French labels)
const CLAIM_DOC_TYPES = {
  ACCIDENT_DECLARATION_RECTO: 'Constat amiable — RECTO',
  ACCIDENT_DECLARATION_VERSO: 'Constat amiable — VERSO',
  CLAIM_PHOTOS: 'Photos sinistre',
  DRIVER_STATEMENT: 'Déclaration conducteur',
  EXPERT_REPORT: 'Rapport expert',
  REPAIR_QUOTE: 'Devis réparation',
  CLAIM_CORRESPONDENCE: 'Correspondance',
};

// --- Claim Declaration Completeness Scoring ---
// Each document type contributes points. Max is capped at 100%.
// Weighted by insurer processing value (inspired by AXA Solaris workflow).
const DOC_SCORE_WEIGHTS = {
  ACCIDENT_DECLARATION_RECTO: 20, // Constat recto — identities, circumstances checkboxes
  ACCIDENT_DECLARATION_VERSO: 15, // Constat verso — diagram, observations, signatures (often forgotten!)
  CLAIM_PHOTOS:               20, // Photos — visual evidence accelerates expertise
  DRIVER_STATEMENT:           10, // Driver's written account
  REPAIR_QUOTE:               10, // Devis from garage — helps estimate provision
  EXPERT_REPORT:               5, // Rare at declaration, but huge value if available
  CLAIM_CORRESPONDENCE:        5, // Police report, witness statement, etc.
};

// When constat is waived (valid reason), its 35 points are redistributed:
// Photos become THE critical evidence, driver statement becomes essential
const DOC_SCORE_WEIGHTS_NO_CONSTAT = {
  CLAIM_PHOTOS:         35, // Photos replace constat as primary evidence
  DRIVER_STATEMENT:     25, // Driver account becomes essential (circumstance proof)
  REPAIR_QUOTE:         10,
  EXPERT_REPORT:         5,
  CLAIM_CORRESPONDENCE: 10, // Police report / PV more important without constat
};

// Claim types where constat amiable is structurally impossible (no third party vehicle)
const CONSTAT_NOT_APPLICABLE_TYPES = ['vol', 'grele', 'panne', 'incendie', 'bris_glace'];

// Valid reasons why constat is unavailable even for collision/corporel
const CONSTAT_WAIVER_REASONS = {
  third_party_fled:   'Tiers en fuite (délit de fuite)',
  animal_collision:   'Collision avec un animal',
  unidentified_third: 'Tiers non identifié',
  parked_vehicle:     'Véhicule stationné (tiers absent)',
  other:              'Autre motif',
};

// Score thresholds and labels
const SCORE_LEVELS = [
  { id: 'insufficient', min: 0,  max: 25,  color: 'red',    bg: 'bg-red-500',    label: 'Insuffisant',     hint: 'Ajoutez au minimum le constat amiable (recto ET verso) pour accélérer le traitement.' },
  { id: 'partial', min: 25, max: 50,  color: 'orange', bg: 'bg-orange-500',  label: 'Partiel',         hint: 'Le constat seul ne suffit pas toujours. Ajoutez des photos du sinistre.' },
  { id: 'correct', min: 50, max: 75,  color: 'yellow', bg: 'bg-yellow-500',  label: 'Correct',         hint: 'Bon dossier. Un devis réparation accélérerait encore le traitement.' },
  { id: 'good', min: 75, max: 90,  color: 'lime',   bg: 'bg-lime-500',    label: 'Bon dossier',     hint: 'Dossier quasi-complet. Très peu d\'allers-retours attendus.' },
  { id: 'optimal', min: 90, max: 101, color: 'green',  bg: 'bg-green-500',   label: 'Dossier optimal', hint: 'Excellent ! L\'assureur pourra traiter ce dossier sans délai.' },
];

// Hints adapt when constat is waived
const SCORE_LEVELS_NO_CONSTAT = [
  { id: 'insufficient', min: 0,  max: 25,  color: 'red',    bg: 'bg-red-500',    label: 'Insuffisant',     hint: 'Sans constat, les photos et la déclaration conducteur sont indispensables.' },
  { id: 'partial', min: 25, max: 50,  color: 'orange', bg: 'bg-orange-500',  label: 'Partiel',         hint: 'Ajoutez la déclaration conducteur pour compenser l\'absence de constat.' },
  { id: 'correct', min: 50, max: 75,  color: 'yellow', bg: 'bg-yellow-500',  label: 'Correct',         hint: 'Bon dossier malgré l\'absence de constat. Un PV ou devis aiderait.' },
  { id: 'good', min: 75, max: 90,  color: 'lime',   bg: 'bg-lime-500',    label: 'Bon dossier',     hint: 'Dossier bien compensé. Peu d\'allers-retours attendus.' },
  { id: 'optimal', min: 90, max: 101, color: 'green',  bg: 'bg-green-500',   label: 'Dossier optimal', hint: 'Excellent ! Dossier complet malgré l\'absence de constat.' },
];

/**
 * Compute the declaration completeness score (0-100).
 * @param {Array} stagedFiles - files with .docType property
 * @param {Object} formData - the claim form data
 * @param {boolean} constatWaived - true if constat is not applicable or waived with reason
 * @returns {{ score: number, level: object, presentTypes: string[], missingHints: string[], constatWaived: boolean }}
 */
function computeCompletenessScore(stagedFiles, formData, constatWaived) {
  const presentTypes = [...new Set(stagedFiles.filter(f => !f.error).map(f => f.docType))];

  // Pick the right weight table
  const weights = constatWaived ? DOC_SCORE_WEIGHTS_NO_CONSTAT : DOC_SCORE_WEIGHTS;
  const levels = constatWaived ? SCORE_LEVELS_NO_CONSTAT : SCORE_LEVELS;

  // Sum document weights
  let docScore = 0;
  for (const type of presentTypes) {
    docScore += weights[type] || 0;
  }

  // Base score from form completeness
  let formScore = 0;
  if (formData.description && formData.description.trim().length > 20) formScore += 10;
  if (formData.garage_name || formData.garage_supplier_id) formScore += 5;

  const raw = Math.min(100, formScore + docScore);
  const level = levels.find(l => raw >= l.min && raw < l.max) || levels[0];

  // Build missing hints
  const hasRecto = presentTypes.includes('ACCIDENT_DECLARATION_RECTO');
  const hasVerso = presentTypes.includes('ACCIDENT_DECLARATION_VERSO');
  const missingHints = [];
  if (!constatWaived) {
    if (!hasRecto) missingHints.push({ key: 'constatRecto', weight: weights.ACCIDENT_DECLARATION_RECTO });
    if (!hasVerso) missingHints.push({ key: 'constatVerso', weight: weights.ACCIDENT_DECLARATION_VERSO });
  }
  if (!presentTypes.includes('CLAIM_PHOTOS')) {
    missingHints.push({ key: 'photos', weight: weights.CLAIM_PHOTOS });
  }
  if (!presentTypes.includes('DRIVER_STATEMENT')) {
    missingHints.push({ key: 'driverStatement', weight: weights.DRIVER_STATEMENT });
  }
  if (!presentTypes.includes('REPAIR_QUOTE')) {
    missingHints.push({ key: 'repairQuote', weight: weights.REPAIR_QUOTE });
  }

  // Special warning: recto without verso
  const constatRectoOnly = hasRecto && !hasVerso && !constatWaived;

  return { score: raw, level, presentTypes, missingHints, constatWaived, constatRectoOnly };
}

/**
 * @typedef {Object} ClaimFormData
 * @property {string} event_date - ISO date of incident (YYYY-MM-DD)
 * @property {string} event_time - Time of incident (HH:MM)
 * @property {string|number} vehicle_id - FK to vehicles.id
 * @property {string|number} [driver_id] - FK to drivers.id
 * @property {string|number} [contract_id] - FK to insurance_contracts.id
 * @property {string|number} [broker_id] - FK to suppliers.id (broker/insurer)
 * @property {'vol'|'collision'|'panne'|'grele'|'incendie'|'corporel'|'bris_glace'} type
 * @property {string} [location] - Where incident occurred
 * @property {string} [description] - Incident description
 * @property {boolean|null} drivable - Can vehicle still be driven?
 * @property {'unknown'|'user_100'|'user_75'|'shared_50'|'user_25'|'not_responsible'} liability
 * @property {string|number} [estimated_amount] - Estimated repair cost
 * @property {string} [garage_name] - Garage name if vehicle taken to garage
 * @property {string} [garage_contact] - Garage contact info
 * @property {string} [garage_address] - Garage address
 * @property {string} [garage_email] - Garage email
 * @property {string} [garage_phone] - Garage phone
 * @property {string} org_id - Organization code
 */

/**
 * ClaimsDeclarationWorkflow - Modal form for declaring new insurance claims
 *
 * Captures incident details and creates a new claim in 'brouillon' status:
 * - Date/time of incident
 * - Vehicle involved (required)
 * - Driver (optional, auto-filled from vehicle if assigned)
 * - Insurance contract (optional)
 * - Incident type, location, description
 * - Whether vehicle is drivable
 * - Liability assessment
 * - Estimated damage amount
 * - Garage info if applicable
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal should close
 * @param {Function} [props.onSuccess] - Called after successful claim creation
 * @param {Function} [props.onMessage] - Called to show toast messages: ({title, message, type}) => void
 * @returns {JSX.Element|null}
 */
function ClaimsDeclarationWorkflow({ isOpen, onClose, onSuccess, onMessage, defaultVehicleId }) {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [intermediaires, setIntermediaires] = useState([]);
  const [garages, setGarages] = useState([]);
  const [garageMode, setGarageMode] = useState('manual'); // 'manual' or 'select'
  const [brokerEmailOverride, setBrokerEmailOverride] = useState(''); // when selected broker has no email
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Wizard tab state: which step is active (1, 2, or 3)
  const [activeStep, setActiveStep] = useState(1);

  // Staged documents (held in memory until claim is created)
  const [stagedFiles, setStagedFiles] = useState([]);
  const [stagedDocType, setStagedDocType] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    event_date: new Date().toISOString().split('T')[0],
    event_time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    vehicle_id: '',
    driver_id: '',
    contract_id: '',
    broker_id: '',
    type: 'collision',
    location: '',
    description: '',
    drivable: null,
    liability: 'unknown',
    estimated_amount: '',
    garage_name: '',
    garage_address: '',
    garage_email: '',
    garage_phone: '',
    garage_supplier_id: null,
    constat_waiver_reason: '', // '' = constat expected, or key from CONSTAT_WAIVER_REASONS
    // Overwritten as soon as a vehicle is selected (see onChange handlers below).
    org_id: getActiveOrg() || 'ORG-A',
  });

  // Step completion derived from formData
  const selectedBroker = formData.broker_id ? intermediaires.find(i => i.id === parseInt(formData.broker_id)) : null;
  const brokerHasEmail = !!(selectedBroker?.contact_email || selectedBroker?.email);
  const brokerEmailValid = brokerHasEmail || (brokerEmailOverride && brokerEmailOverride.includes('@'));
  const step1Complete = !!(formData.event_date && formData.vehicle_id && formData.type && formData.location && formData.broker_id && brokerEmailValid);
  const step2Complete = formData.drivable !== null;
  const step2Unlocked = step1Complete;
  const step3Unlocked = step1Complete && step2Complete;

  // Wizard navigation
  const goToStep = (step) => {
    if (step === 2 && !step2Unlocked) return;
    if (step === 3 && !step3Unlocked) return;
    setActiveStep(step);
  };
  const goNext = () => goToStep(activeStep + 1);
  const goPrev = () => setActiveStep(Math.max(1, activeStep - 1));

  // Load vehicles, drivers, contracts and intermediaires on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [vehiclesRes, driversRes, tripsRes, contractsRes, suppliersRes] = await Promise.all([
          api.vehicle.getAll(),
          api.driver.getAll(),
          api.trip.getAll().catch(() => ({ ok: true, data: [] })),
          api.insurance_contract.getAll(),
          api.supplier.getAll()
        ]);

        if (vehiclesRes.ok) setVehicles((vehiclesRes.data || []).filter(v => v.status !== 'retired' && v.status !== 'exited'));
        if (driversRes.ok) setDrivers((driversRes.data || []).filter(d => d.status !== 'inactive' && d.status !== 'terminated'));
        if (tripsRes.ok) setTrips(tripsRes.data || []);
        if (contractsRes.ok) setContracts(contractsRes.data?.filter(c => c.status === 'active') || []);
        if (suppliersRes.ok) {
          const allSuppliers = suppliersRes.data || [];
          // Filter suppliers to only show brokers and insurers
          setIntermediaires(allSuppliers.filter(s =>
            s.category === 'broker' || s.category === 'insurer'
          ));
          // Filter suppliers for garages
          setGarages(allSuppliers.filter(s => s.category === 'garage'));
        }
      } catch (error) {
        handleApiError(error, 'load', { onMessage, silent: !onMessage });
      } finally {
        setLoadingData(false);
      }
    };

    if (isOpen) {
      // Reset form when opening to avoid stale data from previous unfinalised declaration
      setFormData({
        event_date: new Date().toISOString().split('T')[0],
        event_time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        vehicle_id: '', driver_id: '', contract_id: '', broker_id: '',
        type: 'collision', location: '', description: '', drivable: null,
        liability: 'unknown', estimated_amount: '',
        garage_name: '', garage_address: '', garage_email: '', garage_phone: '',
        garage_supplier_id: null, constat_waiver_reason: '', org_id: getActiveOrg() || 'ORG-A',
      });
      setStagedFiles([]);
      setStagedDocType('');
      setActiveStep(1);
      setGarageMode('manual');
      setBrokerEmailOverride('');
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Resolve current driver for a vehicle: active trip first, then assigned_driver_id fallback
  const resolveDriverForVehicle = useCallback((vehicleId) => {
    if (!vehicleId) return null;
    const vid = Number(vehicleId);
    const activeTrip = trips.find(t =>
      t.vehicle_id === vid && (t.status === 'in_progress' || t.status === 'planned')
    );
    if (activeTrip?.driver_id) return String(activeTrip.driver_id);
    const v = vehicles.find(x => x.id === vid);
    if (v?.assigned_driver_id) return String(v.assigned_driver_id);
    return null;
  }, [trips, vehicles]);

  // Pre-fill vehicle when defaultVehicleId is provided and vehicles are loaded
  useEffect(() => {
    if (defaultVehicleId && vehicles.length > 0 && !formData.vehicle_id) {
      const vehicle = vehicles.find(v => v.id === Number(defaultVehicleId));
      if (vehicle) {
        const driverId = resolveDriverForVehicle(vehicle.id);
        setFormData(prev => ({
          ...prev,
          vehicle_id: vehicle.id,
          org_id: vehicle.org_id || prev.org_id,
          ...(driverId ? { driver_id: driverId } : {})
        }));
      }
    }
  }, [defaultVehicleId, vehicles, trips]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select insurance contract when vehicle changes
  // A vehicle may have multiple contracts (RC, merchandise, extended guarantees)
  const [vehicleContracts, setVehicleContracts] = useState([]);
  useEffect(() => {
    if (!formData.vehicle_id) {
      setVehicleContracts([]);
      return;
    }
    const fetchVehicleContracts = async () => {
      try {
        const res = await vehicleInsuranceAPI.getByVehicle(formData.vehicle_id);
        const links = (res.ok ? res.data : []).filter(vi => !vi.removed_at);
        setVehicleContracts(links);
        // Auto-select primary contract, or first active one
        const primary = links.find(vi => vi.is_primary) || links[0];
        if (primary && !formData.contract_id) {
          const contractObj = contracts.find(c => c.id === primary.contract_id);
          setFormData(prev => ({
            ...prev,
            contract_id: String(primary.contract_id),
            // Auto-fill broker from contract's intermediary if not already set
            ...(contractObj?.intermediary_id && !prev.broker_id
              ? { broker_id: String(contractObj.intermediary_id) }
              : {}),
          }));
        }
      } catch {
        setVehicleContracts([]);
      }
    };
    fetchVehicleContracts();
  }, [formData.vehicle_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // --- Staged file handling ---
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) return t('claims.declaration.extensionNotAllowed', { ext });
    if (file.size > MAX_FILE_SIZE) return t('claims.declaration.fileTooLarge');
    return null;
  };

  const addStagedFiles = useCallback((newFiles) => {
    const processed = newFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      error: validateFile(file),
      docType: stagedDocType || 'CLAIM_PHOTOS', // default type
    }));
    setStagedFiles(prev => [...prev, ...processed]);
  }, [stagedDocType]);

  const removeStagedFile = (index) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addStagedFiles(Array.from(e.dataTransfer.files));
  }, [addStagedFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Upload staged files to the newly created claim
  const uploadStagedFiles = async (claimId) => {
    const validFiles = stagedFiles.filter(f => !f.error);
    if (validFiles.length === 0) return 0;

    const referenceId = `CLM_${String(claimId).padStart(6, '0')}`;
    let uploaded = 0;

    for (const item of validFiles) {
      try {
        // Map recto/verso back to ACCIDENT_DECLARATION for storage, with notes
        const isConstat = item.docType.startsWith('ACCIDENT_DECLARATION');
        const storageType = isConstat ? 'ACCIDENT_DECLARATION' : item.docType;
        const notes = item.docType === 'ACCIDENT_DECLARATION_RECTO' ? 'Recto'
          : item.docType === 'ACCIDENT_DECLARATION_VERSO' ? 'Verso' : undefined;

        const result = await documentAPI.upload(item.file, {
          scope: 'CLAIM',
          reference_id: referenceId,
          document_type: storageType,
          ...(notes ? { notes } : {}),
        });
        if (result.ok) uploaded++;
      } catch (err) {
        console.warn(`Failed to upload ${item.name}:`, err);
      }
    }
    return uploaded;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Auto-create garage supplier if manual entry with a name
      if (garageMode === 'manual' && formData.garage_name && !formData.garage_supplier_id) {
        try {
          const newGarage = await api.supplier.create({
            name: formData.garage_name,
            category: 'garage',
            address: formData.garage_address || null,
            email: formData.garage_email || null,
            phone: formData.garage_phone || null,
            status: 'active',
          });
          if (newGarage.ok && newGarage.data?.id) {
            formData.garage_supplier_id = newGarage.data.id;
          }
        } catch (garageErr) {
          console.warn('Could not auto-create garage supplier:', garageErr);
        }
      }

      const claimData = {
        org_id: formData.org_id,
        vehicle_id: formData.vehicle_id ? parseInt(formData.vehicle_id) : null,
        driver_id: formData.driver_id ? parseInt(formData.driver_id) : null,
        contract_id: formData.contract_id ? parseInt(formData.contract_id) : null,
        broker_id: formData.broker_id ? parseInt(formData.broker_id) : null,
        type: formData.type,
        event_date: formData.event_date,
        event_time: formData.event_time,
        location: formData.location,
        description: formData.description,
        drivable: formData.drivable,
        liability: 'unknown', // Determined by insurer after constat, not at declaration
        estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
        montant_expertise: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
        garage_name: formData.garage_name,
        garage_address: formData.garage_address,
        garage_email: formData.garage_email,
        garage_phone: formData.garage_phone,
        garage_supplier_id: formData.garage_supplier_id ? parseInt(formData.garage_supplier_id) : null,
        constat_waiver_reason: formData.constat_waiver_reason || null,
        // New workflow: start in brouillon, not declared
        status: 'brouillon',
        // Check if bris_glace for simplified workflow
        is_glass_breakage: formData.type === 'bris_glace' ? 1 : 0,
      };

      // If user provided broker email override, persist it on the supplier record
      if (brokerEmailOverride && formData.broker_id) {
        try {
          await api.supplier.update(parseInt(formData.broker_id), { email: brokerEmailOverride });
        } catch (emailSaveErr) {
          console.warn('Could not save broker email to supplier:', emailSaveErr);
        }
      }

      const createResponse = await api.claim.create(claimData);
      const newClaimId = createResponse?.data?.id || createResponse?.id;

      // Upload staged documents now that claim ID exists
      const validStagedFiles = stagedFiles.filter(f => !f.error);
      let uploadedCount = 0;
      if (validStagedFiles.length > 0 && newClaimId) {
        uploadedCount = await uploadStagedFiles(newClaimId);
      }

      // Continuous flow: immediately send declaration + generate .eml with attachments
      // User should NOT see the intermediate brouillon step
      if (newClaimId) {
        try {
          const sendResult = await api.claim.workflowAction(newClaimId, 'envoyer-declaration');
          if (sendResult.ok && sendResult.email?.generated) {
            const attachCount = sendResult.email.attachments?.length || 0;
            // Email is generated and saved in DB — user will download it
            // via the "Envoyer" button in the declaration_prete card (ClaimDetailModal)
            onMessage?.({
              title: t('claims.messages.declarationReadyTitle'),
              message: t('claims.messages.declarationReadyText', { count: attachCount }),
              type: 'info',
            });
          } else {
            onMessage?.({
              title: t('claims.messages.claimCreatedTitle'),
              message: uploadedCount > 0
                ? t('claims.messages.claimCreatedWithDocs', { count: uploadedCount })
                : t('claims.messages.claimCreated'),
              type: 'success'
            });
          }
        } catch (sendErr) {
          console.warn('Auto-send declaration failed, claim saved as brouillon:', sendErr);
          onMessage?.({
            title: t('claims.messages.claimSavedTitle'),
            message: t('claims.messages.claimSavedDraft'),
            type: 'warning'
          });
        }
      }

      onSuccess?.(newClaimId);
      onClose();

      // Reset form
      setFormData({
        event_date: new Date().toISOString().split('T')[0],
        event_time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        vehicle_id: '',
        driver_id: '',
        contract_id: '',
        broker_id: '',
        type: 'collision',
        location: '',
        description: '',
        drivable: null,
        liability: 'unknown',
        estimated_amount: '',
        garage_name: '',
        garage_address: '',
        garage_email: '',
        garage_phone: '',
        garage_supplier_id: null,
        constat_waiver_reason: '',
        org_id: getActiveOrg() || 'ORG-A',
      });
      setGarageMode('manual');
      setStagedFiles([]);
      setStagedDocType('');
      setActiveStep(1);
      setBrokerEmailOverride('');
    } catch (error) {
      handleApiError(error, 'create', { onMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full h-[95vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">{t('claims.declaration.title')}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-3" data-form="claim-details">
          {loadingData ? (
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-600">{t('claims.declaration.loadingData')}</div>
            </div>
          ) : (
            <>
              {/* Step Tabs */}
              <div className="sticky top-0 bg-white z-10 pb-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  {[
                    { step: 1, label: t('claims.declaration.step1'), icon: FileText, complete: step1Complete, unlocked: true },
                    { step: 2, label: t('claims.declaration.step2'), icon: Car, complete: step2Complete, unlocked: step2Unlocked },
                    { step: 3, label: t('claims.declaration.step3'), icon: Upload, complete: stagedFiles.filter(f => !f.error).length > 0, unlocked: step3Unlocked },
                  ].map(({ step, label, icon: Icon, complete, unlocked }, idx) => (
                    <React.Fragment key={step}>
                      {idx > 0 && (
                        <div className={`flex-1 h-0.5 mx-2 ${idx <= activeStep - 1 ? 'bg-green-400' : 'bg-gray-200'}`} />
                      )}
                      <button
                        onClick={() => goToStep(step)}
                        disabled={!unlocked}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeStep === step
                            ? 'bg-red-50 text-red-700 border-2 border-red-400 shadow-sm'
                            : complete
                            ? 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                            : unlocked
                            ? 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                            : 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
                        }`}
                      >
                        {complete && activeStep !== step ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : !unlocked ? (
                          <Lock className="w-3.5 h-3.5" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                        <span>{t('claims.declaration.stepLabel', { step, label })}</span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-1">
                  <div
                    className="bg-red-500 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${((activeStep - 1) / 2) * 100}%` }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {/* ═══════════════════════════════════════════════ */}
                {/* STEP 1: Description du sinistre                */}
                {/* ═══════════════════════════════════════════════ */}
                {activeStep === 1 && (
                  <div className="space-y-2">
                          {/* Date and Time */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.eventDate')}</label>
                              <input
                                type="date"
                                value={formData.event_date}
                                onChange={(e) => updateFormData('event_date', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.eventTime')}</label>
                              <input
                                type="time"
                                value={formData.event_time}
                                onChange={(e) => updateFormData('event_time', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          {/* Vehicle and Driver */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                                <Car className="w-4 h-4" />
                                {t('claims.declaration.vehicle')}
                              </label>
                              <select
                                value={formData.vehicle_id}
                                onChange={(e) => {
                                  const vid = e.target.value;
                                  const v = vehicles.find(x => x.id === Number(vid));
                                  const driverId = resolveDriverForVehicle(vid);
                                  setFormData(prev => ({
                                    ...prev,
                                    vehicle_id: vid,
                                    org_id: v?.org_id || prev.org_id,
                                    ...(driverId ? { driver_id: driverId } : {}),
                                  }));
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">{t('claims.declaration.vehiclePlaceholder')}</option>
                                {vehicles.map(vehicle => (
                                  <option key={vehicle.id} value={vehicle.id}>
                                    {vehicle.make} {vehicle.model} - {vehicle.license_plate}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {t('claims.declaration.driver')}
                              </label>
                              <select
                                value={formData.driver_id}
                                onChange={(e) => updateFormData('driver_id', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">{t('claims.declaration.driverPlaceholder')}</option>
                                {drivers.map(driver => (
                                  <option key={driver.id} value={driver.id}>
                                    {driver.first_name} {driver.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Contract and Intermediary */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('claims.declaration.contract')}
                                {vehicleContracts.length > 1 && (
                                  <span className="text-xs text-blue-600 ml-1">{t('claims.declaration.linkedContracts', { count: vehicleContracts.length })}</span>
                                )}
                              </label>
                              <select
                                value={formData.contract_id}
                                onChange={(e) => {
                                  const contractId = e.target.value;
                                  updateFormData('contract_id', contractId);
                                  // Auto-fill broker from selected contract
                                  if (contractId) {
                                    const contractObj = contracts.find(c => c.id === parseInt(contractId));
                                    if (contractObj?.intermediary_id) {
                                      updateFormData('broker_id', String(contractObj.intermediary_id));
                                      setBrokerEmailOverride('');
                                    }
                                  }
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">{t('claims.declaration.contractPlaceholder')}</option>
                                {(() => {
                                  const linkedIds = new Set(vehicleContracts.map(vc => vc.contract_id));
                                  const linked = contracts.filter(c => linkedIds.has(c.id));
                                  const others = contracts.filter(c => !linkedIds.has(c.id));
                                  return (
                                    <>
                                      {linked.length > 0 && <optgroup label={t('claims.declaration.vehicleContractsGroup')}>
                                        {linked.map(contract => {
                                          const vi = vehicleContracts.find(vc => vc.contract_id === contract.id);
                                          const badge = vi?.is_primary ? ' ★' : '';
                                          const tpl = vi?.coverage_model?.name ? ` (${vi.coverage_model.name})` : '';
                                          return (
                                            <option key={contract.id} value={contract.id}>
                                              {contract.policy_number} - {contract.insurer_name}{tpl}{badge}
                                            </option>
                                          );
                                        })}
                                      </optgroup>}
                                      {others.length > 0 && <optgroup label={t('claims.declaration.otherContractsGroup')}>
                                        {others.map(contract => (
                                          <option key={contract.id} value={contract.id}>
                                            {contract.policy_number} - {contract.insurer_name}
                                          </option>
                                        ))}
                                      </optgroup>}
                                    </>
                                  );
                                })()}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.intermediary')}</label>
                              <select
                                value={formData.broker_id}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateFormData('broker_id', val);
                                  // Reset email override when broker changes
                                  setBrokerEmailOverride('');
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">{t('claims.declaration.intermediaryPlaceholder')}</option>
                                {intermediaires.map(inter => (
                                  <option key={inter.id} value={inter.id}>
                                    {inter.name} ({inter.category === 'broker' ? t('claims.declaration.categoryBroker') : t('claims.declaration.categoryInsurer')})
                                  </option>
                                ))}
                              </select>
                              {/* Broker email warning + inline input */}
                              {(() => {
                                if (!formData.broker_id) return null;
                                const selectedBroker = intermediaires.find(i => i.id === parseInt(formData.broker_id));
                                const brokerEmail = selectedBroker?.contact_email || selectedBroker?.email;
                                if (brokerEmail) {
                                  return (
                                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" />
                                      {t('claims.declaration.brokerEmailKnown', { email: brokerEmail })}
                                    </p>
                                  );
                                }
                                return (
                                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1.5">
                                    <p className="text-xs text-amber-800 font-medium flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      {t('claims.declaration.brokerEmailMissing')}
                                    </p>
                                    <input
                                      type="email"
                                      value={brokerEmailOverride}
                                      onChange={(e) => setBrokerEmailOverride(e.target.value)}
                                      placeholder={t('claims.declaration.brokerEmailPlaceholder')}
                                      className="w-full px-3 py-1.5 border border-amber-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-amber-600">
                                      {t('claims.declaration.brokerEmailHint')}
                                    </p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Type + Location */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.claimType')}</label>
                              <select
                                value={formData.type}
                                onChange={(e) => updateFormData('type', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="collision">{t('claims.types.collision')}</option>
                                <option value="vol">{t('claims.types.vol')}</option>
                                <option value="panne">{t('claims.types.panne')}</option>
                                <option value="grele">{t('claims.types.grele')}</option>
                                <option value="incendie">{t('claims.types.incendie')}</option>
                                <option value="corporel">{t('claims.types.corporel')}</option>
                                <option value="bris_glace">{t('claims.types.bris_glace')}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.claimLocation')}</label>
                              <input
                                type="text"
                                value={formData.location}
                                onChange={(e) => updateFormData('location', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder={t('claims.declaration.claimLocationPlaceholder')}
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.description')}</label>
                            <textarea
                              value={formData.description}
                              onChange={(e) => updateFormData('description', e.target.value)}
                              rows={2}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder={t('claims.declaration.descriptionPlaceholder')}
                            />
                          </div>

                    {!step1Complete && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t('claims.declaration.requiredFieldsHint')}
                      </p>
                    )}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════ */}
                {/* STEP 2: Etat du vehicule et garage             */}
                {/* ═══════════════════════════════════════════════ */}
                {activeStep === 2 && (
                  <div className="space-y-2">
                          {/* Drivable and Estimated Amount */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.drivable')}</label>
                              <select
                                value={formData.drivable === null ? '' : formData.drivable ? '1' : '0'}
                                onChange={(e) => updateFormData('drivable', e.target.value === '' ? null : e.target.value === '1')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">{t('claims.declaration.drivableUnspecified')}</option>
                                <option value="1">{t('common.yes')}</option>
                                <option value="0">{t('common.no')}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.estimatedAmount')}</label>
                              <input
                                type="number"
                                value={formData.estimated_amount}
                                onChange={(e) => updateFormData('estimated_amount', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder={t('claims.declaration.estimatedAmountPlaceholder')}
                              />
                            </div>
                          </div>

                          {/* Garage Information */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-blue-900">{t('claims.declaration.garageSection')}</h4>
                              <div className="flex bg-white rounded-lg border border-blue-200 overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setGarageMode('select')}
                                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                                    garageMode === 'select'
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                >
                                  <Search className="w-3 h-3" />
                                  {t('claims.declaration.garageModeSelect')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGarageMode('manual')}
                                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                                    garageMode === 'manual'
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                >
                                  <PenLine className="w-3 h-3" />
                                  {t('claims.declaration.garageModeManual')}
                                </button>
                              </div>
                            </div>

                            {garageMode === 'select' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.garageSelectLabel')}</label>
                                <select
                                  value={formData.garage_supplier_id || ''}
                                  onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const garage = garages.find(g => g.id === parseInt(selectedId));
                                    if (garage) {
                                      setFormData(prev => ({
                                        ...prev,
                                        garage_supplier_id: garage.id,
                                        garage_name: garage.name || '',
                                        garage_address: [garage.address, garage.postal_code, garage.city].filter(Boolean).join(', ') || '',
                                        garage_email: garage.email || '',
                                        garage_phone: garage.phone || '',
                                      }));
                                    } else {
                                      updateFormData('garage_supplier_id', null);
                                    }
                                  }}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="">{t('claims.declaration.garageSelectPlaceholder')}</option>
                                  {garages.map(g => (
                                    <option key={g.id} value={g.id}>
                                      {g.name}{g.city ? ` - ${g.city}` : ''}
                                    </option>
                                  ))}
                                </select>
                                {garages.length === 0 && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    {t('claims.declaration.noGarageReferenced')}
                                  </p>
                                )}
                                {formData.garage_supplier_id && (
                                  <div className="mt-2 text-sm text-gray-600 bg-white rounded-lg p-3 space-y-1">
                                    <div><span className="font-medium">{t('claims.declaration.garageName')}</span> {formData.garage_name}</div>
                                    {formData.garage_address && <div><span className="font-medium">{t('claims.declaration.garageAddress')}</span> {formData.garage_address}</div>}
                                    {formData.garage_email && <div><span className="font-medium">{t('claims.declaration.garageEmail')}</span> {formData.garage_email}</div>}
                                    {formData.garage_phone && <div><span className="font-medium">{t('claims.declaration.garagePhone')}</span> {formData.garage_phone}</div>}
                                  </div>
                                )}
                              </div>
                            )}

                            {garageMode === 'manual' && (
                              <>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.garageNameLabel')}</label>
                                    <input
                                      type="text"
                                      value={formData.garage_name}
                                      onChange={(e) => updateFormData('garage_name', e.target.value)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder={t('claims.declaration.garageNamePlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.garageAddressLabel')}</label>
                                    <input
                                      type="text"
                                      value={formData.garage_address}
                                      onChange={(e) => updateFormData('garage_address', e.target.value)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder={t('claims.declaration.garageAddressPlaceholder')}
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.garageEmailLabel')}</label>
                                    <input
                                      type="email"
                                      value={formData.garage_email}
                                      onChange={(e) => updateFormData('garage_email', e.target.value)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder={t('claims.declaration.garageEmailPlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.garagePhoneLabel')}</label>
                                    <input
                                      type="tel"
                                      value={formData.garage_phone}
                                      onChange={(e) => updateFormData('garage_phone', e.target.value)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder={t('claims.declaration.garagePhonePlaceholder')}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                  </div>
                )}

                {/* ═══════════════════════════════════════════════ */}
                {/* STEP 3: Documentation du sinistre              */}
                {/* ═══════════════════════════════════════════════ */}
                {activeStep === 3 && (
                  <div className="space-y-2">
                          {/* Documents - Staging Zone with Completeness Score */}
                          {(() => {
                            const constatNotApplicable = CONSTAT_NOT_APPLICABLE_TYPES.includes(formData.type);
                            const constatWaived = constatNotApplicable || !!formData.constat_waiver_reason;
                            const completeness = computeCompletenessScore(stagedFiles, formData, constatWaived);
                            const activeWeights = constatWaived ? DOC_SCORE_WEIGHTS_NO_CONSTAT : DOC_SCORE_WEIGHTS;

                            return (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-step="claim-documents">
                                {/* Header + Score gauge */}
                                <div className="flex items-center justify-between">
                                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                                    <Paperclip className="w-5 h-5" />
                                    {t('claims.score.sectionTitle')}
                                  </h4>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-500">{t('claims.score.completeness')}</span>
                                    <span className={`text-sm font-bold ${
                                      completeness.score >= 90 ? 'text-green-600' :
                                      completeness.score >= 75 ? 'text-lime-600' :
                                      completeness.score >= 50 ? 'text-yellow-600' :
                                      completeness.score >= 25 ? 'text-orange-600' : 'text-red-600'
                                    }`}>{completeness.score}%</span>
                                  </div>
                                </div>

                                {/* Progress bar */}
                                <div className="relative">
                                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ease-out ${completeness.level.bg}`}
                                      style={{ width: `${completeness.score}%` }}
                                    />
                                  </div>
                                  <div className="flex justify-between mt-1">
                                    <span className={`text-xs font-medium ${
                                      completeness.score >= 75 ? 'text-green-700' :
                                      completeness.score >= 50 ? 'text-yellow-700' : 'text-red-700'
                                    }`}>
                                      {t(`claims.score.levels.${completeness.level.id}`, { defaultValue: completeness.level.label })}
                                    </span>
                                    <span className="text-xs text-gray-400">{t('claims.score.target')}</span>
                                  </div>
                                </div>

                                {/* Hint / nudge message */}
                                <p className={`text-sm rounded-md px-3 py-2 ${
                                  completeness.score >= 75 ? 'bg-green-50 text-green-800 border border-green-200' :
                                  completeness.score >= 50 ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
                                  'bg-red-50 text-red-800 border border-red-200'
                                }`}>
                                  {t(`claims.score.hints.${constatWaived ? 'noConstat' : 'default'}.${completeness.level.id}`, { defaultValue: completeness.level.hint })}
                                </p>

                                {/* Constat amiable waiver (only for collision/corporel types) */}
                                {!constatNotApplicable && (
                                  <div className={`rounded-lg p-3 ${formData.constat_waiver_reason ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-gray-200'}`}>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={!!formData.constat_waiver_reason}
                                        onChange={(e) => updateFormData('constat_waiver_reason', e.target.checked ? 'third_party_fled' : '')}
                                        className="mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                      />
                                      <div>
                                        <span className="text-sm font-medium text-gray-800">{t('claims.declaration.constatUnavailable')}</span>
                                        <span className="text-xs text-gray-500 ml-1">{t('claims.declaration.constatUnavailableHint')}</span>
                                      </div>
                                    </label>
                                    {formData.constat_waiver_reason && (
                                      <div className="mt-2 ml-6">
                                        <select
                                          value={formData.constat_waiver_reason}
                                          onChange={(e) => updateFormData('constat_waiver_reason', e.target.value)}
                                          className="w-full px-3 py-1.5 border border-amber-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        >
                                          {Object.entries(CONSTAT_WAIVER_REASONS).map(([key, label]) => (
                                            <option key={key} value={key}>{t(`claims.constatWaiver.${key}`, { defaultValue: label })}</option>
                                          ))}
                                        </select>
                                        <p className="text-xs text-amber-700 mt-1">
                                          {t('claims.declaration.constatWaiverHint')}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Auto-waiver notice for non-collision types */}
                                {constatNotApplicable && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600">
                                    {t('claims.declaration.constatNotApplicable', { type: t(`claims.types.${formData.type}`, { defaultValue: formData.type }).toLowerCase() })}
                                  </div>
                                )}

                                {/* Recto-only warning */}
                                {completeness.constatRectoOnly && (
                                  <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 flex items-start gap-2 animate-pulse-once">
                                    <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-sm font-semibold text-orange-900">
                                        {t('claims.declaration.versoMissingTitle')}
                                      </p>
                                      <p className="text-xs text-orange-800 mt-0.5">
                                        {t('claims.declaration.versoMissingText')}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Missing document hints */}
                                {completeness.score < 90 && completeness.missingHints.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {completeness.missingHints.map((hint) => (
                                      <span key={hint.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white border border-gray-300 text-gray-600">
                                        <Upload className="w-3 h-3" />
                                        {t(`claims.score.missing.${hint.key}`, { weight: hint.weight })}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Document type selector */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('claims.declaration.docTypeLabel')}</label>
                                  <select
                                    value={stagedDocType}
                                    onChange={(e) => setStagedDocType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="">{t('claims.declaration.docTypeDefault')}</option>
                                    {Object.entries(CLAIM_DOC_TYPES).map(([key, label]) => {
                                      if (key.startsWith('ACCIDENT_DECLARATION') && constatWaived) return null;
                                      const hasIt = completeness.presentTypes.includes(key);
                                      const weight = activeWeights[key] || 0;
                                      return (
                                        <option key={key} value={key}>
                                          {hasIt ? '\u2713 ' : ''}{t('claims.declaration.docTypeOption', { label: t(`claims.docTypes.${key}`, { defaultValue: label }), weight })}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>

                                {/* Drop zone */}
                                <div
                                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                                  onDragOver={handleDragOver}
                                  onDrop={handleDrop}
                                  onClick={() => fileInputRef.current?.click()}
                                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                                    isDragging
                                      ? 'border-blue-500 bg-blue-100'
                                      : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-gray-50'
                                  }`}
                                >
                                  <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                                  <p className="text-sm text-gray-600">
                                    {isDragging ? t('claims.declaration.dropHere') : t('claims.declaration.dropZoneHint')}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">{t('claims.declaration.dropZoneFormats')}</p>
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept={ALLOWED_EXTENSIONS.join(',')}
                                    onChange={(e) => { addStagedFiles(Array.from(e.target.files)); e.target.value = ''; }}
                                    className="hidden"
                                  />
                                </div>

                                {/* Staged files list */}
                                {stagedFiles.length > 0 && (
                                  <div className="border rounded-lg bg-white divide-y">
                                    {stagedFiles.map((item, index) => (
                                      <div key={index} className="flex items-center gap-3 px-3 py-2">
                                        <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                                          <p className="text-xs text-gray-500">
                                            {formatSize(item.size)}
                                            {' — '}
                                            <span className="text-blue-600">{t(`claims.docTypes.${item.docType}`, { defaultValue: CLAIM_DOC_TYPES[item.docType] || CLAIM_DOC_TYPES.CLAIM_PHOTOS })}</span>
                                            {item.error && <span className="text-red-500 ml-2">{item.error}</span>}
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); removeStagedFile(index); }}
                                          className="p-1 text-gray-400 hover:text-red-500"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                    <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                      {t('claims.declaration.filesReady', { count: stagedFiles.filter(f => !f.error).length })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* SLA Notice */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-800 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                              <span><strong>{t('claims.declaration.slaNoticeStrong')}</strong> {t('claims.declaration.slaNoticeText')}</span>
                            </p>
                          </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
          <button onClick={onClose} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">
            {t('common.actions.cancel')}
          </button>
          <div className="flex items-center gap-3">
            {activeStep > 1 && (
              <button
                onClick={goPrev}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                {'\u2190 '}{t('common.actions.previous')}
              </button>
            )}
            {activeStep < 3 && (
              <button
                onClick={goNext}
                disabled={activeStep === 1 ? !step1Complete : !step2Complete}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  (activeStep === 1 ? step1Complete : step2Complete)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('common.actions.next')}{' \u2192'}
              </button>
            )}
            {step1Complete && (
              <button
                onClick={handleSubmit}
                disabled={submitting || loadingData}
                className={`px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                  submitting || loadingData
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? t('common.states.saving') : t('claims.declaration.submit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClaimsDeclarationWorkflow;
